import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import AddAudio from '../AddAudio/AddAudio';

const getCount = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const isSuccessfulResult = (result) => {
  if (!result) return false;
  if (result.success === false) return false;
  return getCount(result.failed) <= 0;
};

const getCompleted = (result) => (
  getCount(result?.generated)
  + getCount(result?.failed)
  + getCount(result?.skipped)
);

const ElevenLabsBulkAudioDialog = ({
  open,
  title,
  locale,
  localeLabel,
  defaultBulk,
  defaultLimit,
  disabled,
  loading,
  progress,
  result,
  error,
  successMessage,
  onRun,
  onRunHaitian,
  onRunPolly,
  onClose,
}) => {
  const [autoOpenKey, setAutoOpenKey] = useState(0);
  const [processOpen, setProcessOpen] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) {
      setProcessOpen(false);
      return;
    }

    if (!locale || disabled) {
      setProcessOpen(true);
      return;
    }

    setProcessOpen(false);
    setAutoOpenKey((value) => value + 1);
  }, [disabled, locale, open]);

  useEffect(() => {
    if (
      !open
      || loading
      || error
      || !isSuccessfulResult(result)
      || result?.hasMoreAudioTargets === true
    ) return undefined;

    closeTimerRef.current = setTimeout(() => {
      setProcessOpen(false);
      onClose();
    }, 5000);

    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [error, loading, onClose, open, result]);

  const runHandler = (selection) => {
    if (disabled || loading) return;

    setProcessOpen(true);

    onRun({
      ...selection,
      bulk: defaultBulk !== false,
      limit: defaultLimit || 1,
    });
  };

  const haitianRunHandler = (selection) => {
    if (disabled || loading || typeof onRunHaitian !== 'function') return;

    setProcessOpen(true);

    onRunHaitian({
      ...selection,
      bulk: defaultBulk !== false,
      limit: defaultLimit || 1,
    });
  };

  const pollyRunHandler = (selection) => {
    if (disabled || loading || typeof onRunPolly !== 'function') return;

    setProcessOpen(true);
    onRunPolly({
      ...selection,
      bulk: defaultBulk !== false,
      limit: defaultLimit || 1,
    });
  };

  const status = useMemo(() => {
    if (error || getCount(result?.failed || result?.failedCount) > 0 || result?.success === false) return 'Error';
    if (loading) return 'Running';
    if (result) return 'Done';
    return 'Ready';
  }, [error, loading, result]);

  const matched = getCount(result?.matched || result?.matchedCount);
  const generated = getCount(result?.generated || result?.generatedCount);
  const failed = getCount(result?.failed || result?.failedCount);
  const skipped = getCount(result?.skipped || result?.skippedCount);
  const completed = getCompleted(result);
  const total = matched > 0 ? matched : Math.max(completed, generated + failed + skipped);
  const progressValue = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const canClose = !loading;

  const savedFile = (Array.isArray(result?.savedFiles) && result.savedFiles[0]) || null;
  const failedFile = (Array.isArray(result?.failedFiles) && result.failedFiles[0]) || null;
  const failedFileError = failedFile?.error || '';
  const savedPath = result?.savedKey || savedFile?.s3Key || '';
  const savedUrl = result?.savedUrl || savedFile?.s3Url || '';
  const attemptedPath = result?.attemptedKey || failedFile?.attemptedKey || '';
  const attemptedUrl = result?.attemptedUrl || failedFile?.attemptedUrl || '';
  const targetFolder = result?.targetS3Path || result?.s3Path || failedFile?.s3Path || savedFile?.s3Path || '';
  const targetFolderUrl = result?.targetS3Url || '';
  const hasPathDebug = Boolean(targetFolder || savedPath || savedUrl || attemptedPath || attemptedUrl);
  const audioInventory = Array.isArray(result?.audioInventory) ? result.audioInventory : [];
  const audioDebug = Array.isArray(result?.audioDebug) ? result.audioDebug : [];
  const generatedAudioDebug = Array.isArray(result?.generatedAudioDebug) ? result.generatedAudioDebug : [];
  const audioDebugRows = audioInventory.length
    ? audioInventory
    : (generatedAudioDebug.length ? generatedAudioDebug : audioDebug);
  const isDone = Boolean(result && !loading && !error && isSuccessfulResult(result) && result.hasMoreAudioTargets !== true);
  const remainingLabel = result
    ? (Number.isFinite(Number(result.remainingCount))
      ? String(Number(result.remainingCount))
      : (result.hasMoreAudioTargets ? 'More' : '0'))
    : '-';
  const defaultSuccessMessage = generated > 0
    ? `Finished. All missing audio files have been created. Generated ${generated} audio file(s).`
    : 'Finished. No missing audio files were found.';
  const reportMessage = error
    || failedFileError
    || result?.error
    || (isDone ? (successMessage || defaultSuccessMessage) : '')
    || progress
    || (loading ? 'Generating audio...' : 'Ready.');

  if (!open && !processOpen) return null;

  return (
    <>
      {open && !processOpen && locale && !disabled && (
        <div style={{ display: 'none' }}>
          <AddAudio
            autoOpenKey={autoOpenKey}
            useFileBucket
            path=""
            removeOldOnChange={false}
            current=""
            onChangeHandler={() => {}}
            associatedText={`${title || 'Add Audio'} batch`}
            versionType={`immersion_${locale}`}
            withOverlay
            onGenerateElevenLabsOverride={runHandler}
            onGeneratePollyOverride={typeof onRunPolly === 'function' ? pollyRunHandler : undefined}
            onGenerateHaitianOverride={haitianRunHandler}
            onModalClose={onClose}
          />
        </div>
      )}

      <Dialog
        open={processOpen}
        onClose={canClose ? onClose : undefined}
        fullWidth
        maxWidth="sm"
        disableBackdropClick={loading}
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>{title || 'Add Audio'}</span>
            <Typography
              variant="subtitle1"
              color={status === 'Error' ? 'error' : 'textSecondary'}
              style={status === 'Done' ? { color: '#2e7d32', fontWeight: 600 } : undefined}
            >
              {status}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {reportMessage}
          </Typography>

          <Box my={2}>
            {loading && !total ? (
              <LinearProgress />
            ) : (
              <LinearProgress variant="determinate" value={progressValue} />
            )}
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gridGap={12} mb={2}>
            <Typography variant="body2">Found: <strong>{matched}</strong></Typography>
            <Typography variant="body2">Generated: <strong>{generated}</strong></Typography>
            <Typography variant="body2">Failed: <strong>{failed}</strong></Typography>
            <Typography variant="body2">Skipped: <strong>{skipped}</strong></Typography>
            <Typography variant="body2">Locale: <strong>{localeLabel || locale || '-'}</strong></Typography>
            <Typography variant="body2">Remaining: <strong>{remainingLabel}</strong></Typography>
          </Box>

          {(error || failedFileError || result?.error) && (
            <Box mt={1}>
              <Typography variant="body2" color="error">{error || failedFileError || result?.error}</Typography>
            </Box>
          )}

          {audioDebugRows.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Activity audio map
              </Typography>

              <Box maxHeight={220} overflow="auto" border={1} borderColor="divider" borderRadius={4} p={1}>
                {audioDebugRows.slice(0, 80).map((item, index) => {
                  const rowDone = item.status === 'replaced-verified'
                    || item.status === 'done'
                    || item.status === 'completed'
                    || (item.persistenceVerified === true && Boolean(item.filename || item.newFilename || item.finalAudio));
                  const mode = String(item.generationMode || '').trim().toLowerCase();
                  const modeLabel = mode === 'stream' ? 'Stream' : (mode === 'standard' ? 'Standard' : '');
                  const fallbackLabel = item.generationFallbackFrom === 'stream' ? ' (Stream fallback)' : '';
                  const elapsedMs = Number(item.generationElapsedMs || 0);

                  return (
                    <Box key={`${item.audioPath || item.dataPath || index}-${index}`} mb={1}>
                      <Typography variant="caption" display="block">
                        <strong>{index + 1}. {item.role || 'audio'}</strong> —{' '}
                        <span style={rowDone ? { color: '#2e7d32', fontWeight: 600 } : undefined}>
                          {rowDone ? 'Done' : (item.status || (item.filename ? 'generated' : ''))}
                        </span>
                      </Typography>
                      {modeLabel && (
                        <Typography variant="caption" display="block" style={{ fontSize: '0.7rem' }}>
                          TTS: <strong>{modeLabel}{fallbackLabel}</strong>
                          {elapsedMs > 0 ? ` · ${(elapsedMs / 1000).toFixed(1)}s` : ''}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block">
                        Text: <strong>{item.text || '-'}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        Text path: {item.textPath || '-'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Audio path: {item.audioPath || item.dataPath || '-'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Old audio: <strong>{item.oldFilename || item.currentAudio || '-'}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        New audio: <strong>{item.finalAudio || item.newFilename || item.filename || '-'}</strong>
                      </Typography>
                      {(item.currentS3Key || item.oldS3Key) && (
                        <Typography variant="caption" display="block">
                          Old S3: {item.oldS3Key || item.currentS3Key}
                        </Typography>
                      )}
                      {(item.finalS3Key || item.savedS3Key || item.s3Key) && (
                        <Typography variant="caption" display="block">
                          New S3: {item.finalS3Key || item.savedS3Key || item.s3Key}
                        </Typography>
                      )}
                      {item.error && (
                        <Typography variant="caption" display="block" color="error">
                          Error: {item.error}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {hasPathDebug && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                S3 save target
              </Typography>

              {targetFolder && (
                <Typography variant="caption" display="block">
                  Target folder: <strong>{targetFolder}</strong>
                </Typography>
              )}

              {targetFolderUrl && (
                <Typography variant="caption" display="block">
                  Target URL folder: <strong>{targetFolderUrl}</strong>
                </Typography>
              )}

              {attemptedPath && (
                <Typography variant="caption" display="block" color={status === 'Error' ? 'error' : 'textSecondary'}>
                  Attempted file path: <strong>{attemptedPath}</strong>
                </Typography>
              )}

              {attemptedUrl && (
                <Typography variant="caption" display="block" color={status === 'Error' ? 'error' : 'textSecondary'}>
                  Attempted file URL: <strong>{attemptedUrl}</strong>
                </Typography>
              )}

              {savedPath && (
                <Typography variant="caption" display="block">
                  Saved file path: <strong>{savedPath}</strong>
                </Typography>
              )}

              {savedUrl && (
                <Typography variant="caption" display="block">
                  Saved file URL: <strong>{savedUrl}</strong>
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={!canClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ElevenLabsBulkAudioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  locale: PropTypes.string,
  localeLabel: PropTypes.string,
  defaultBulk: PropTypes.bool,
  defaultLimit: PropTypes.number,
  allowBulk: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  progress: PropTypes.string,
  result: PropTypes.object,
  error: PropTypes.string,
  successMessage: PropTypes.string,
  preview: PropTypes.object,
  previewLoading: PropTypes.bool,
  previewError: PropTypes.string,
  previewTitle: PropTypes.string,
  safeModeLabel: PropTypes.string,
  bulkModeLabel: PropTypes.string,
  onPreview: PropTypes.func,
  onRun: PropTypes.func.isRequired,
  onRunHaitian: PropTypes.func,
  onRunPolly: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ElevenLabsBulkAudioDialog.defaultProps = {
  title: 'Add Audio',
  locale: '',
  localeLabel: '',
  defaultBulk: true,
  defaultLimit: 1,
  allowBulk: false,
  disabled: false,
  loading: false,
  progress: '',
  result: null,
  error: '',
  successMessage: '',
  preview: null,
  previewLoading: false,
  previewError: '',
  previewTitle: '',
  safeModeLabel: '',
  bulkModeLabel: '',
  onPreview: null,
  onRunHaitian: null,
  onRunPolly: null,
};

export default ElevenLabsBulkAudioDialog;
